import React, { useState, useCallback, useEffect } from 'react';
import { useComplianceStore } from '../../../store/complianceStore';
import { useComplianceTypeListStore } from '../../../store/masterDataStore';
import FileUpload from '../../../components/FileUpload/FileUpload';
import { useDropzone } from 'react-dropzone';
import { useS3Upload } from "../../../utils/s3Client";
import { useAssetInventoryStore } from '../../../store/store';

interface ComplianceCertificateFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  initialData?: {
    assetId: string;
    assetName: string;
    certificateType: string;
  } | null;
}

const ComplianceCertificateForm: React.FC<ComplianceCertificateFormProps> = ({ onSuccess, onCancel, initialData }) => {
  const { addCertificate, updateCertificate, loading: storeLoading } = useComplianceStore();
  const { uploadFileToS3, progress, uploading, error: uploadError } = useS3Upload();
  const { assetInventory, fetchAssetInventory } = useAssetInventoryStore();
  const { complianceTypeList, fetchAllComplianceTypeList } = useComplianceTypeListStore();
  
  const [formData, setFormData] = useState({
    assetId: initialData?.assetId || '',
    assetName: initialData?.assetName || '',
    certificateType: initialData?.certificateType || '',
    issueDate: '',
    expiryDate: '',
  });

  const [files, setFiles] = useState<File[] | null>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAssetInventory();
    fetchAllComplianceTypeList();
  }, [fetchAssetInventory, fetchAllComplianceTypeList]);

  // Use dynamic compliance types instead of hardcoded list
  const certificateTypes = complianceTypeList.map(type => type.complianceTypeName);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-fill asset name if ID is selected
    if (name === 'assetId') {
      const selectedAsset = assetInventory?.find((opt: any) => opt._id === value);
      if (selectedAsset) {
        setFormData(prev => ({ ...prev, assetName: selectedAsset.name }));
      }
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false, 
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc', '.docx'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls', '.xlsx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'application/vnd.ms-powerpoint': ['.ppt', '.pptx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.assetId || !formData.certificateType || !formData.issueDate || !formData.expiryDate) {
      setError("Please fill in all required fields.");
      return;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(formData.expiryDate);
    const issueDate = new Date(formData.issueDate);

    if (expiryDate < today) {
        // Allow updating expired certificates if we are just fixing records, but here we enforce future dates for new validity.
        // However, user complaint was about expiry status. If they are renewing, expiry MUST be in future.
      setError("Expiry Date must be in the future.");
      return;
    }

    if (expiryDate < issueDate) {
      setError("Expiry Date must be equal to or later than Issue Date.");
      return;
    }

    const fileToUpload = files && files.length > 0 ? files[0] : null;
    let uploadedUrl = undefined;

    if (fileToUpload) {
      try {
        uploadedUrl = await uploadFileToS3(fileToUpload);
      } catch (err) {
        setError("Failed to upload file. Please try again.");
        return;
      }
    }

    let success = false;
    
    // If initialData has an ID, it means we are updating/renewing an existing certificate
    if (initialData && 'id' in initialData) {
         success = await updateCertificate((initialData as any).id, {
            assetId: formData.assetId,
            assetName: formData.assetName,
            certificateType: formData.certificateType,
            issueDate: formData.issueDate,
            expiryDate: formData.expiryDate,
            documentUrl: uploadedUrl,
         });
    } else {
         success = await addCertificate({
            assetId: formData.assetId,
            assetName: formData.assetName,
            certificateType: formData.certificateType,
            issueDate: formData.issueDate,
            expiryDate: formData.expiryDate,
            documentUrl: uploadedUrl, 
         });
    }

    if (success) {
      onSuccess();
    }
  };

  const isLoading = storeLoading || uploading;
  const inputClass = "w-full border-2 border-gray-400 rounded p-2 focus:outline-none focus:border-blue-500";

  return (
    <div className="flex flex-col max-h-[85vh] h-full">
      {/* Header */}
      <div className="flex-none px-6 pt-6">
        <h2 className="text-2xl font-semibold">
          {initialData ? "Renew Compliance Certificate" : "Upload Compliance Certificate"}
        </h2>
        <hr className="mt-4 border-t-2 border-gray-300" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6">
        {(error || uploadError) && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
            <p>{error || uploadError}</p>
          </div>
        )}

        <form id="certificateForm" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Asset Selection */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">Asset <span className="text-red-500">*</span></label>
            <select
              name="assetId"
              value={formData.assetId}
              onChange={handleChange}
              disabled={!!initialData}
              className={`${inputClass} ${initialData ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">Select Asset</option>
              {assetInventory?.map((opt: any) => (
                <option key={opt._id} value={opt._id}>{opt.name}</option>
              ))}
            </select>
          </div>

          {/* Certificate Type */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">Certificate Type <span className="text-red-500">*</span></label>
            <select
              name="certificateType"
              value={formData.certificateType}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">Select Type</option>
              {certificateTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Issue Date */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">Issue Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              name="issueDate"
              value={formData.issueDate}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          {/* Expiry Date */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">Expiry Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleChange}
              min={formData.issueDate}
              className={inputClass}
            />
          </div>

          {/* File Upload - Spans full width */}
          <div className="md:col-span-2">
            <label className="block mb-1 font-medium text-gray-700">Upload Certificate</label>
            <FileUpload
              mode="create"
              files={files}
              onFilesChange={setFiles}
              existingAttachments={[]}
              onRemoveExistingAttachment={() => {}}
              onPreviewFile={() => {}} 
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              maxFileSizeMB={10}
              uploading={uploading}
              uploadProgress={progress}
              disabled={isLoading}
            />
          </div>
        </form>
      </div>

      {/* Footer Actions */}
      <div className="flex-none py-4 px-6 bg-slate-500 flex justify-end gap-4 rounded-b-xl">
        <button
          type="button"
          className="bg-gray-300 text-gray-800 px-6 py-2 rounded-full font-medium hover:bg-gray-400 transition-colors"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="certificateForm"
          disabled={isLoading}
          className={`bg-teal-600 text-white px-6 py-2 rounded-full font-medium hover:bg-teal-700 transition-colors flex items-center gap-2 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {uploading ? 'Uploading...' : 'Saving...'}
            </>
          ) : 'Submit'}
        </button>
      </div>
    </div>
  );
};

export default ComplianceCertificateForm;